import { isEqual } from "lodash/fp";

import { FormControlLabel, Radio, Typography } from "@mui/material";

import type { CreditPackage } from "@app/analytics/account/Billing/types";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { formatCurrency } from "@app/utils/utils";

import Alert from "@rui/Alert";
import Stack, { Row } from "@rui/Stack";

import { CREDIT_PACKAGES } from "../constants";
import {
  CheckoutButton,
  DetailsHeading,
  PanelContentInnerContainer,
  PanelContentOuterContainer,
  PanelMainContent,
  PanelSection,
  PurchaseOption,
} from "../styles";

interface BillingCreditCheckoutDetailsProps {
  isLoading: boolean;
  onChange: (newPackage: CreditPackage) => void;
  onContinue: () => void;
  selectedPackage: CreditPackage;
}

/**
 * Step 1 of the credit checkout process.
 * Includes selecting how many credits the user would like to purchase.
 * */
const BillingCreditCheckoutDetails = ({
  isLoading,
  onChange,
  onContinue,
  selectedPackage,
}: BillingCreditCheckoutDetailsProps) => {
  const { inWorkspace } = useWorkspace();
  const { subscription } = useBillingContext();

  const hasFreeMonthlyCreditAllowance =
    subscription.settings.monthlyFreeUploadCredits > 0;
  const showAlert = inWorkspace || hasFreeMonthlyCreditAllowance;

  const handleSelectPackage = (newPackage: CreditPackage) => () => {
    onChange(newPackage);
  };

  return (
    <PanelContentOuterContainer>
      <PanelContentInnerContainer>
        {/* Shim to keep the main content in the center and the button at the bottom */}
        <Stack />
        <PanelMainContent>
          <PanelSection>
            <DetailsHeading>Upload credit packs</DetailsHeading>
            {CREDIT_PACKAGES.map((pack) => {
              const isSelected = isEqual(pack, selectedPackage);

              return (
                <PurchaseOption
                  key={pack.quantity}
                  selected={isSelected}
                  sx={{ py: 0.5, px: 1.5 }}
                  onClick={handleSelectPackage(pack)}
                >
                  <Row variant="apart">
                    <FormControlLabel
                      control={<Radio checked={isSelected} />}
                      label={`${pack.quantity} minutes`}
                    />
                    <Typography variant="bodyPrimaryMed">
                      {formatCurrency(pack.price)}
                    </Typography>
                  </Row>
                </PurchaseOption>
              );
            })}
          </PanelSection>
          {showAlert && (
            <Alert
              severity="info"
              sx={{
                fontFamily: (theme) => theme.typography.fontFamily,
                maxWidth: "400px",
                margin: "auto",
              }}
            >
              {inWorkspace
                ? "Upload credits are accessible to anyone in the Workspace, and do not expire."
                : "Upload credits do not expire."}
              {hasFreeMonthlyCreditAllowance &&
                ` Your complimentary ${subscription.settings.monthlyFreeUploadCredits} monthly credits will always be used first, if available.`}
            </Alert>
          )}
        </PanelMainContent>
        <CheckoutButton
          data-test-id="billing-continue-to-payment"
          onClick={onContinue}
          loading={isLoading}
        >
          Continue to payment info
        </CheckoutButton>
      </PanelContentInnerContainer>
    </PanelContentOuterContainer>
  );
};

export default BillingCreditCheckoutDetails;
